import {
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  DialogTitle as MuiDialogTitle,
  DialogContent as MuiDialogContent,
  DialogActions as MuiDialogActions,
  SxProps,
  Theme,
} from '@mui/material';
import { CloseButton } from '../../Buttons';
import { forwardRef, ReactNode, useId } from 'react';

/** Reason passed to onClose when the dialog is closed (MUI reasons plus our close button). */
export type DialogCloseReason = 'backdropClick' | 'escapeKeyDown' | 'closeButtonClick';

export interface DialogProps
  extends Omit<MuiDialogProps, 'title' | 'content' | 'onClose' | 'children'> {
  /**
   * Dialog title - can be a string or React node
   */
  title?: ReactNode;

  /**
   * Whether to show close button in title
   * @default false
   */
  showCloseButton?: boolean;

  /**
   * Custom close button icon
   */
  closeIcon?: ReactNode;

  /**
   * Callback when the dialog requests close (backdrop click, escape key, or close button).
   * Receives the event and reason: 'backdropClick' | 'escapeKeyDown' | 'closeButtonClick'
   * so consumers can distinguish explicit close-button clicks from backdrop/escape.
   */
  onClose?: (event: object, reason: DialogCloseReason) => void;

  /**
   * Dialog content - rendered inside DialogContent
   */
  content?: ReactNode;

  /**
   * Dialog actions - rendered inside DialogActions
   */
  actions?: ReactNode;

  /**
   * Whether dialog is open
   */
  open: boolean;

  /**
   * Custom styles for the paper component
   */
  paperSx?: SxProps<Theme>;

  /**
   * Custom styles for the DialogTitle
   */
  titleSx?: SxProps<Theme>;

  /**
   * Custom styles for the DialogContent
   */
  contentSx?: SxProps<Theme>;

  /**
   * Custom styles for the DialogActions
   */
  actionsSx?: SxProps<Theme>;

  /**
   * Whether this is an error dialog (affects title color)
   * @default false
   */
  isError?: boolean;
}

/**
 * Dialog Component
 *
 * A flexible dialog component that wraps MUI Dialog with standardized styling.
 * Preserves MUI's built-in transitions, focus management, and ARIA attributes.
 * Supports:
 * - Optional title with close button
 * - Custom styling and responsive behavior
 * - Error state styling
 * - Full MUI Dialog props support
 *
 * @example
 * ```tsx
 * <Dialog
 *   open={open}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   content={<Typography>Are you sure?</Typography>}
 *   actions={
 *     <>
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *       <Button onClick={handleClose}>Cancel</Button>
 *     </>
 *   }
 *   fullWidth
 *   maxWidth="xs"
 * />
 * ```
 */
export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    title,
    showCloseButton = false,
    closeIcon,
    onClose,
    content,
    actions,
    open,
    paperSx,
    titleSx,
    contentSx,
    actionsSx,
    isError = false,
    ...other
  },
  ref
) {
  const handleClose = (event: object, reason: DialogCloseReason) => {
    onClose?.(event, reason);
  };

  const titleId = useId();
  const contentId = useId();
  const { 'aria-labelledby': ariaLabelledby, 'aria-describedby': ariaDescribedby, ...rest } = other;

  return (
    <MuiDialog
      ref={ref}
      open={open}
      onClose={handleClose as MuiDialogProps['onClose']}
      aria-labelledby={ariaLabelledby ?? (title ? titleId : undefined)}
      aria-describedby={ariaDescribedby ?? (content ? contentId : undefined)}
      PaperProps={{
        sx: [
          (theme: Theme) => ({
            boxShadow: '-40px 40px 80px -8px rgba(0, 0, 0, 0.24)',
            width: '100%',
            '&.MuiPaper-rounded': {
              borderRadius: '15px',
            },
            '&.MuiDialog-paperFullScreen': {
              borderRadius: '15px',
            },
            '@media (max-width: 600px)': {
              margin: theme.spacing(2),
            },
            '@media (max-width: 663.95px)': {
              '&.MuiDialog-paperWidthXs.MuiDialog-paperScrollBody': {
                maxWidth: '100%',
              },
            },
          }),
          ...(paperSx ? [paperSx] : []),
        ] as SxProps<Theme>,
      }}
      {...rest}
    >
      {title && (
        <MuiDialogTitle
          id={titleId}
          sx={
            [
              (theme: Theme) => ({
                color: isError ? 'error.main' : 'secondary.main',
                padding: theme.spacing(3),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }),
              ...(titleSx ? [titleSx] : []),
            ] as SxProps<Theme>
          }
        >
          {title}
          {showCloseButton && (
            <CloseButton
              icon={closeIcon}
              color="inherit"
              onClick={(e) => handleClose(e, 'closeButtonClick')}
              aria-label="close"
              sx={{ ml: 2 }}
            />
          )}
        </MuiDialogTitle>
      )}
      {content && (
        <MuiDialogContent
          id={contentId}
          sx={
            [
              (theme: Theme) => ({
                padding: theme.spacing(0, 3),
              }),
              ...(contentSx ? [contentSx] : []),
            ] as SxProps<Theme>
          }
        >
          {content}
        </MuiDialogContent>
      )}
      {actions && (
        <MuiDialogActions
          sx={
            [
              (theme: Theme) => ({
                padding: theme.spacing(3),
                '& > :not(:first-of-type)': {
                  marginLeft: theme.spacing(1.5),
                },
              }),
              ...(actionsSx ? [actionsSx] : []),
            ] as SxProps<Theme>
          }
        >
          {actions}
        </MuiDialogActions>
      )}
    </MuiDialog>
  );
});

Dialog.displayName = 'Dialog';
